<template>
  <v-card class="financial-products-table">
    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        Filtered results ({{ filteredResults.length }} of {{ filteredItems.length }})
        <v-tooltip
          v-if="!hasResultFilters"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="secondary"
              v-on="on"
            >
              <v-icon>
                filter_list
              </v-icon>
            </v-btn>
          </template>
          <span>Filters</span>
        </v-tooltip>
        <v-tooltip
          v-else
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="primary"
              v-on="on"
              @click="resetResultsFilters"
            >
              <v-icon>
                restore
              </v-icon>
            </v-btn>
          </template>
          <span>Reset filters</span>
        </v-tooltip>
      </div>
      <v-select
        v-model="resultFilters.instrument"
        class="ml-5"
        primary
        :items="financialInstrument"
        label="Financial Instrument"
        item-value="value"
        clearable
      />

      <v-text-field
        v-model="resultFilters.searchResults"

        class="ml-5"
        append-icon="search"
        label="filter results"
      />
    </v-card-title>
    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          color="secondary"
          fab
          dark
          absolute
          top
          right
          @click.stop="openCreationDialog"
          v-on="on"
        >
          <v-icon>add</v-icon>
        </v-btn>
      </template>
      <span>Create new FP</span>
    </v-tooltip>
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredResults"
      :loading="loading"
      :expand="expand"
      :pagination.sync="pagination"
      :rows-per-page-items="rowsPerPageItems"
      item-key="id"
      class="elevation-1"
    >
      <template v-slot:items="props">
        <tr
          :active="props.selected"
          @click="props.expanded = !props.expanded"
        >
          <td class="text-xs-center">
            {{ props.item.id }}
          </td>
          <td class="text-xs-center">
            {{ props.item.project_name }}
          </td>
          <td class="text-xs-center">
            {{ props.item.instrument }}
          </td>
          <td class="text-xs-center">
            {{ props.item.security_id }}
          </td>
          <td class="text-xs-center">
            {{ props.item.regulationType }}
          </td>
          <td class="text-xs-center">
            {{ props.item.stage }}
          </td>
          <td class="text-xs-center">
            {{ props.item.funding_target }}
          </td>
          <td class="text-xs-center">
            {{ props.item.notional_value }}
          </td>
          <td class="text-xs-center">
            {{ props.item.created_at ? formatDateSmall(props.item.created_at) : null }}
          </td>
        </tr>
      </template>
      <template v-slot:expand="props">
        <FinancialProductRowDetail
          :item="props.item"
        />
      </template>
    </v-data-table>
    <create-draft
      v-if="isOpenCreationDialog"
      :dialog="isOpenCreationDialog"
      :submit-action="submitCreateDraft"
      :cancel-action="cancelCreationModal"
      :error="createError"
    />
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapState } from 'vuex';
import { formatDateSmall } from '@/utils/dateFormats';
import { toQueryString, setQuery } from '@/helpers';
import { FinancialInstrumentsEnum } from '@/enum/financialProducts/FinancialInstruments';
import FinancialProductRowDetail from './shared/FinancialProductRowDetail';
import CreateDraft from '@/components/financialProducts/CreateDraft';

export default {
  name: 'FinancialProductsTable',
  components: {
    FinancialProductRowDetail,
    CreateDraft,
  },
  data() {
    return {
      expand: false,
      isLoadTableData: false,
      selected: [],
      createError: '',
      isOpenCreationDialog: false,
      filters: {
        createdAt: [],
        state: [],
      },
      resultFilters: {
        status: null,
        instrument: null,
        searchResults: null,
      },
      headers: [
        { text: 'ID', align: 'center', value: 'id' },
        { text: 'Project Name', align: 'center', value: 'project_name' },
        { text: 'Instrument', align: 'center', value: 'instrument' },
        { text: 'Security ID', align: 'center', value: 'security_id' },
        { text: 'Regulation Type', align: 'center', value: 'regulationType' },
        { text: 'Stage', align: 'center', value: 'stage' },
        { text: 'Funding Target', align: 'center', value: 'funding_target' },
        { text: 'Notional Value', align: 'center', value: 'notional_value' },
        { text: 'Created at', align: 'center', value: 'created_at' },
      ],
      pagination: {
        page: 1,
        rowsPerPage: 25,
        sortBy: 'stage',
        descending: false,
      },
      rowsPerPageItems: [
        { text: '25', value: 25 },
        { text: '50', value: 50 },
        { text: '100', value: 100 },
        { text: 'All', value: -1 },
      ],
      formatDateSmall,
    };
  },
  computed: {
    ...mapActions(['createDraft']),
    ...mapState({
      loading: state => state.financialProductsStore.loading,
      financialProducts: state => state.financialProductsStore.financialProducts,
    }),
    filteredItems() {
      return this.financialProducts.filter(d => Object.keys(this.filters).every((f) => {
        if (this.filters[f]) {
          return (
            this.filters[f].length < 1 ||
            String(this.filters[f]).includes(d[f])
          );
        }

        return true;
      }));
    },
    filteredResults() {
      if (this.hasResultFilters) {
        return this.filteredItems.filter((item) => {
          let hasStatus = true;
          let hasSearch = true;
          let hasInstrument = true;

          const { status, searchResults, instrument } = this.resultFilters;

          if (instrument) {
            hasInstrument = item.instrument === instrument;
          }

          if (status) {
            hasStatus = item.status.status === status;
          }

          if (searchResults) {
            hasSearch = JSON.stringify(item)
              .toLowerCase()
              .includes(searchResults.toLowerCase());
          }

          return (!!hasInstrument && !!hasStatus && !!hasSearch) || false;
        });
      }

      return this.filteredItems;
    },
    financialInstrument() {
      return Object.keys(FinancialInstrumentsEnum).map(key => ({
        text: FinancialInstrumentsEnum[key],
        value: FinancialInstrumentsEnum[key],
      }));
    },
    hasResultFilters() {
      return !!this.resultFilters.status
        || !!this.resultFilters.searchResults
        || !!this.resultFilters.instrument;
    },
  },
  watch: {
    async financialProducts() {
      this.getDefaultFilters();
    },
    instrument: {
      immediate: false,
      handler(instrument, oldInstrument) {
        if (instrument !== oldInstrument) {
          setQuery(
            {
              instrument,
            },
            this.$route,
            this.$router,
          );
          this.refreshTable();
        }
      },
    },
  },
  mounted() {
    this.getFinancialProducts(toQueryString({ instrument: undefined }));
  },
  methods: {
    ...mapActions(['getFinancialProducts', 'createDraftCall']),
    refreshTable() {
      const query = toQueryString({
        instrument: undefined,
      });

      if (typeof window.LIT !== 'undefined') {
        clearTimeout(window.LIT);
      }

      window.LIT = setTimeout(() => {
        this.getFinancialProducts(query);
      }, 1000);
    },
    getDefaultFilters() {
      if (this.$route.query) {
        Object.keys(this.$route.query).forEach((item) => {
          let value = this.$route.query[item];

          if (Object.prototype.hasOwnProperty.call(this.filters, item)) {
            if (typeof this.filters[item] === 'string') {
              this.filters[item] = value;
            } else if (String(this.$route.query[item]).includes(';')) {
              value = this.$route.query[item].split(';');
              this.filters[item] = value;
            } else {
              this.filters[item].push(value);
            }
          }
        });
      }
    },
    resetResultsFilters() {
      this.resultFilters = _.mapValues(this.resultFilters, () => null);
    },
    async submitCreateDraft(data) {
      try {
        const response = await this.createDraftCall(data);
        if (response.status === 200) {
          await this.$router.push(
            `/financial-products/product/${data.instrument}/${data.security_id_type}/${data.security_id}`,
          );
        }
        this.isOpenCreationDialog = false;
      } catch (e) {
        if (e.response && e.response.data) {
          this.createError = e.response.data;
        } else {
          this.createError = 'Fehler bei der Draft-Erstellung';
        }

        this.isOpenCreationDialog = true;
      }
    },
    openCreationDialog() {
      this.isOpenCreationDialog = true;
    },
    cancelCreationModal() {
      this.isOpenCreationDialog = false;
      this.createError = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.financial-products-table{
  a {
    text-decoration: none;
    color: black;
  }

  .toolbar {
    background-color: #1a4c7d;
    color: white;
  }

  .infoLabel {
    margin-left: 16px;
  }

  .countLabel {
    color: #fff;
  }
  .expanded-details {
    background-color: #eaeaea;
    padding: 20px;
  }

  .v-select{
        flex: 1;
  }

  table.v-table {
    td:not(:first-child),
    th:not(:first-child) {
      padding: 0 1% !important;
    }
  }

  .v-card__title.subheading{
    .v-icon{
      margin-right:10px;
    }
  }
  .v-input{
    &.security_wallet{
      min-width: 350px;
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/financialProducts/FinancialProductsTable.vue